import { AlertTriangle, ArrowLeft, Home } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../index';

interface NotFoundProps {
    /** Tipo de entidad que no se encontró (ej: "Mercancía", "Producto", "Usuario") */
    entityType: string;
    /** ID o identificador que se estaba buscando */
    entityId?: string | number;
    /** Título personalizado. Si no se proporciona, se genera automáticamente */
    title?: string;
    /** Mensaje personalizado. Si no se proporciona, se genera automáticamente */
    message?: string;
    /** Ruta a la que regresar. Si no se proporciona, va al listado de la entidad */
    backPath?: string;
    /** Texto del botón de regreso */
    backButtonText?: string;
    /** Si mostrar botón para ir al inicio */
    showHomeButton?: boolean;
    /** Texto del botón de inicio */
    homeButtonText?: string;
    /** Ruta del botón de inicio */
    homePath?: string;
    /** Icono personalizado */
    icon?: React.ReactNode;
    /** Data testid para testing */
    testId?: string;
}

const NotFound: React.FC<NotFoundProps> = ({
    entityType,
    entityId,
    title,
    message,
    backPath,
    backButtonText = 'Volver atrás',
    showHomeButton = true,
    homeButtonText = 'Ir al inicio',
    homePath = '/',
    icon,
    testId = 'not-found'
}) => {
    const navigate = useNavigate();

    // Generar título automático si no se proporciona
    const finalTitle = title || `${entityType} no encontrada`;

    // Generar mensaje automático si no se proporciona
    const finalMessage = message || (
        entityId
            ? `La ${entityType.toLowerCase()} con ID "${entityId}" no existe o no tienes permisos para verla.`
            : `La ${entityType.toLowerCase()} solicitada no existe o no tienes permisos para verla.`
    );

    // Manejar navegación hacia atrás
    const handleGoBack = () => {
        if (backPath) {
            navigate(backPath);
        } else {
            navigate(-1); // Ir atrás en el historial
        }
    };

    // Manejar navegación al inicio
    const handleGoHome = () => {
        navigate(homePath);
    };

    return (
        <div className="min-h-[400px] flex items-center justify-center p-6" data-testid={testId}>
            <div className="text-center space-y-6 max-w-md">
                {/* Icono */}
                <div className="flex justify-center">
                    {icon || (
                        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                            <AlertTriangle className="w-8 h-8 text-destructive" />
                        </div>
                    )}
                </div>

                {/* Contenido */}
                <div className="space-y-3">
                    <h2 className="text-2xl font-semibold text-foreground">
                        {finalTitle}
                    </h2>
                    <p className="text-muted-foreground">
                        {finalMessage}
                    </p>
                </div>

                {/* Acciones */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button
                        variant="outline"
                        onClick={handleGoBack}
                        className="flex items-center gap-2"
                        data-testid="go-back-button"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {backButtonText}
                    </Button>

                    {showHomeButton && (
                        <Button
                            variant="primary"
                            onClick={handleGoHome}
                            className="flex items-center gap-2"
                            data-testid="go-home-button"
                        >
                            <Home className="w-4 h-4" />
                            {homeButtonText}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotFound; 